import React from 'react';
import { Stack, Button, Box, useTheme } from '@mui/material';
import { glassStyle, glassUnsupportedSx } from 'utils/glass';
import { EllipsisVertical, SearchIcon } from 'lucide-react';
import { Input } from '@repo/ui';
// Filter / Sort / clear-sort are ONE shared pair with the certificates list —
// see `ToolbarControl` for the measured divergence this ended.
import { FilterControl, SortControl } from 'components/UI/ToolbarControl';
import { fieldFocusSx } from 'components/UI/fieldMetrics';
import { PAGE_GUTTER, PAGE_GUTTER_MOBILE } from 'constants/layout';

export interface SearchFilterSortBarProps {
  /**
   * What this bar acts on — "scans", "provenance", "activity". Used only for the
   * Filter and Sort accessible names ("Sort provenance, 2 active").
   *
   * Required, and that is the fix: the word was hardcoded to "scans" in both
   * labels, so a screen reader on the PROVENANCE screen announced "Sort scans"
   * over a list of owners, and on Activity likewise. Three callers, one of which
   * was right by accident. A required prop makes the next caller state it.
   */
  subject: string;
  query: string;
  onQueryChange: (value: string) => void;
  filterBadgeCount?: number;
  onOpenFilter: () => void;
  sortBadgeCount?: number;
  onOpenSort: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  showSortClear?: boolean;
  onClearSort?: () => void;
  onMoreOptionsClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Hide the sort control. Sort order is meaningless against a cluster map, so the
   * Scans tab drops it in map mode (VERA-476) to keep the row to search + filter.
   */
  showSort?: boolean;
  /**
   * Optional view-mode control (e.g. the Scans tab's Map/List toggle), rendered as the
   * last item in the action group. Lives here rather than in its own row so a 375px
   * screen isn't three rows of chrome deep before any content (VERA-476).
   */
  viewToggle?: React.ReactNode;
  /**
   * Pin the bar to the top while scrolling. Defaults true because both callers are list
   * tabs — but the Scans tab turns it OFF in map view: a map doesn't scroll, so pinning
   * there would just eat map area for no benefit (VERA-476).
   */
  sticky?: boolean;
  /**
   * Where the bar pins, in px from the top.
   *
   * Non-zero when something else is already pinned above it — on a certificate
   * sub-view the page header pins first, and the two together form ONE block
   * with the hairline at its bottom (parity with the certificates page's
   * HeaderBar, which is a single element containing title, search and
   * controls). Left at 0 the bar would pin at the same 0px as the header and
   * slide underneath it.
   */
  stickyTop?: number;
}

export const SearchFilterSortBar: React.FC<SearchFilterSortBarProps> = ({
  subject,
  query,
  onQueryChange,
  filterBadgeCount = 0,
  onOpenFilter,
  sortBadgeCount = 0,
  onOpenSort,
  showSortClear = false,
  onClearSort,
  onMoreOptionsClick,
  showSort = true,
  viewToggle,
  sticky = true,
  stickyTop = 0,
}) => {
  // The divider belongs on the bar only once it has actually been scrolled to the top —
  // sitting there inline, unscrolled, it's arbitrary chrome. See the effect below for
  // how "stuck" is detected.
  const theme = useTheme();
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = React.useState(false);

  React.useEffect(() => {
    if (!sticky) {
      setIsStuck(false);
      return;
    }

    const check = () => {
      const el = barRef.current;
      // Compare against WHERE THE BAR PINS, not 0. With something pinned above
      // it (the sub-view page header), the bar reaches its resting place while
      // the sentinel is still `stickyTop` px below the viewport top — so a
      // `<= 0` test leaves a window where the bar is visibly pinned and still
      // transparent, with rows sliding through it.
      if (el) setIsStuck(el.getBoundingClientRect().top <= stickyTop);
    };
    check();

    // Two independent signals, because neither alone is sufficient:
    //
    // 1. IntersectionObserver on the sentinel is the primary. It fires on any layout
    //    change, so it doesn't care HOW the page moved — a programmatic scrollTo, an
    //    anchor jump, or content above the bar resizing all release the divider. The
    //    sentinel is 1px tall, NOT 0: a zero-height element is a zero-AREA target and
    //    the observer never reliably fires for it (that was the first attempt's bug).
    // 2. The scroll listener is the backstop for the initial frames, before the
    //    observer's first callback lands.
    const sentinel = sentinelRef.current;
    const observer = sentinel
      /* rootMargin pulls the observation edge DOWN to the pin line, for the same
         reason the scroll test compares against `stickyTop`. */
      ? new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
          threshold: 0,
          rootMargin: `-${stickyTop}px 0px 0px 0px`,
        })
      : null;
    observer?.observe(sentinel!);

    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [sticky]);

  // Sticky (VERA-476). This bar is the only chrome on the page that ACTS ON the content
  // you're scrolling — from 40 rows down, filtering otherwise means scrolling back to
  // the top and losing your place. Tabs and title stay scrollable: they're navigation
  // and orientation, used on arrival, not repeatedly mid-scroll.
  //
  // Lives on the component rather than a per-caller prop because both usages (Scans and
  // Provenance) are list tabs. Product has no bar, so nothing is imposed on it — the
  // rule is "the list controls bar sticks", with no special-casing.
  return (
    <>
      {/* Stuck-detection sentinel: sits in normal flow directly above the bar, so it
          scrolls out of view at exactly the moment the bar pins. 1px, not 0 (see the
          effect above). Only rendered when sticky, so the static bar costs nothing. */}
      {sticky && <Box ref={sentinelRef} sx={{ height: '1px' }} aria-hidden />}
      <Stack
        ref={barRef}
      // Inline, NOT in `sx`: the sx-driven border kept painting grey at scroll 0 even
      // with isStuck === false, because emotion served a cached class for this element
      // rather than re-serializing on the state flip. An inline style always wins and is
      // never class-cached, so the divider tracks `isStuck` deterministically (VERA-476).
      /*
       * Background is set INLINE for the same reason the border is: emotion
       * serves a cached class for this element across the isStuck flip, so an
       * sx-driven value can paint the wrong state. Inline always wins and is
       * never class-cached.
       *
       * GLASS once stuck, transparent at rest — the same material as BottomNav,
       * the certificate header and the Activity toolbar, so every pinned surface
       * in this app is one material rather than four approximations. It was an
       * opaque `background.default` panel, which covered the rows passing under
       * it instead of letting them show through.
       *
       * `saturate` is the half that does the work and the half that is easy to
       * drop: blur alone greys out what passes beneath and reads as frosted
       * plastic.
       */
      style={{
        borderBottomColor: sticky && isStuck ? theme.palette.grey[200] : 'transparent',
        /* `glassStyle`, not `glassSx`, precisely because of the paragraph above:
           this call site cannot use sx for its fill. The `@supports` half CANNOT
           be written inline, so it is spread into `sx` below — the two together
           are one treatment, and dropping either leaves a bar that is see-through
           where the material is unsupported. */
        ...glassStyle(theme, sticky && isStuck),
      }}
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
      sx={theme => ({
        display: 'flex',
        position: sticky ? 'sticky' : 'static',
        top: stickyTop,
        zIndex: 2,
        // The negative-margin/padding pair keeps the pinned surface edge to edge;
        // the fill itself is inline above (glass when stuck, transparent at rest).
        // Degrade honestly where the material is unsupported: an opaque panel
        // rather than a 82%-transparent one with controls unreadable over rows.
        // The other half of the inline `glassStyle` above — same `active`
        // expression, and it has to stay the same or the two states disagree.
        ...glassUnsupportedSx(theme, sticky && isStuck),
        // Asymmetric on purpose (VERA-978). The tab chips above end with a 4px margin of
        // their own, so a symmetric 12px top read as 17px against the tab row; 8px lands
        // the visual gap at 13px. The bottom stays 12px so the distance down to the first
        // row is unchanged.
        pt: '8px',
        pb: '12px',
        /*
         * Bleeds to the COLUMN's edge, so it must cancel whatever gutter the
         * container above it is applying — which is `PAGE_GUTTER_MOBILE` at `xs`
         * and `PAGE_GUTTER` at `md`, not a flat 16.
         *
         * Measured before this was made responsive: this bar pinned at 88 → 1192
         * inside a column running 80 → 1200, so two pinned surfaces on the same
         * screen (this and `PageHeader`'s) had different widths and an 8px sliver
         * of unglassed page showed down each side of this one.
         */
        mx: { xs: `-${PAGE_GUTTER_MOBILE}px`, md: `-${PAGE_GUTTER}px` },
        px: { xs: `${PAGE_GUTTER_MOBILE}px`, md: `${PAGE_GUTTER}px` },
        mb: '4px',
        // Width/style only; the COLOR is set inline above so the space is always
        // reserved (no 1px jump when the divider appears) but the paint tracks isStuck.
        borderBottom: '1px solid transparent',
        transition: 'border-color .15s ease',
      })}
    >
      <Stack
        sx={{
          flexGrow: 1,
          minWidth: 0,
          mr: { md: 1, xs: 0 },
          /*
            ONE THIRD OF THIS TOOLBAR'S OWN CONTENT COLUMN, at md and up.
            Was `{ md: 520, lg: 640 }` — two hand-picked numbers that, measured at a
            1280 viewport, put the field at 640 of a 1072px column (60%), i.e. running
            most of the way across the page while the certificates list's equivalent
            field stops after its first card.

            ## Why a PERCENTAGE and not `HeaderBar`'s expression

            `HeaderBar` caps its field at
              `calc((min(100vw - SIDE_NAV_WIDTH, APP_MAX_WIDTH) - 48px - 32px) / 3)`
            and that expression must NOT be copied here. It encodes the LIST's
            geometry — a three-column card grid, the gutter split across three
            elements, and the rail being in flow on that page. None of those hold on
            a sub-view: this column is `CONTENT_MAX_WIDTH` with `PAGE_GUTTER`, the
            rail is not rendered
            (measured: the column centres in the whole 1280, 80 → 1200), and there is
            no card grid for the field's right edge to land on. Pasting the viewport
            form here would bind the field to a rail this page does not have.

            A percentage resolves against this Stack's containing block — the bar's
            content box, which after the bar's `mx/px` bleed is exactly the page's
            content column. So the cap is derived from the container it actually sits
            in and survives any change to the column width, including the rail being
            switched on for these screens.

            ## What "parity with certificates" means here, and the numbers

            The two pages have different columns (the list is an INDEX at 1280 less a
            48px gutter and a 200px rail; a sub-view is READING at 1120 less 24px), so
            "parity" cannot be both the same fraction AND the same pixels. This takes
            the FRACTION, because that is what the list's own rule is — one card of
            three — and it is the half that stays true when either column moves.

            Measured at a 1280 viewport:
              certificates list   column 984,  field 317px  (32.3% — one card of three)
              this bar            column 1072, field 357px  (33.3%)
            40px apart, both reading as "the left third of the page". Before this
            change the sub-view field was 640px, twice the list's.
          */
          maxWidth: { md: '33.333%' },
        }}
      >
        <Input
          onChange={(value: string) => onQueryChange(value)}
          placeholder='Search'
          // minWidth:0 lets the field actually shrink when the action group grows
          // (VERA-476 added the view toggle). Without it the row overflows the
          // viewport and the last control is clipped instead.
          // ...fieldFocusSx replaces `@repo/ui` Input's OWN focus treatment, which
          // is the brand-purple ring. This field sits one screen away from the
          // certificates toolbar search, which carried a different halo — so the
          // app showed two focus treatments for the same control. Both now take
          // the single one defined in `fieldMetrics`: the border goes to the
          // tenant accent, with one 6% ring behind it.
          //
          // ⚠️ A PLAIN OBJECT, never `sx={theme => …}`. `@repo/ui`'s Input merges
          // with `sx={{ ...readOnlySx, ...(sx || {}) }}` — an object spread — and
          // spreading a FUNCTION yields `{}`, so a callback form is accepted by
          // the compiler and then silently dropped at runtime. `theme` comes from
          // the `useTheme()` already in scope above.
          sx={{ height: '48px', minWidth: 0, '& .MuiInputBase-root': { minWidth: 0 }, ...fieldFocusSx(theme) }}
          startAdornment={
            /*
              KEEP the magnifier, add the gap.
              
              The alternative was dropping it, and the icon earns its place: it is
              the fastest signal that a rounded rect is a SEARCH field rather than
              any other text input, and this app has several. The cramping was a
              spacing bug, not an argument against the glyph.
              
              8px between the icon and the placeholder, and the icon inset from
              the field's own edge, so "Search" is not butted against the
              magnifier. 20px rather than 24 — at 24 the glyph was nearly the cap
              height of the text beside it and read as a second element competing
              with the placeholder rather than as its label.
            */
            <div className='flex items-center justify-center' style={{ marginRight: 8 }}>
              <SearchIcon color='#7E7E7E' size={20} />
            </div>
          }
        />
      </Stack>

      {/*
        Filter and Sort are `components/UI/ToolbarControl` — the SAME two
        components the certificates list's `HeaderBar` renders.

        The comment that stood here claimed "spacing + button padding match the
        Certificates page HeaderBar exactly". They did not, and had not for some
        time. Measured at 1280 on a clean tree: Sort 82x49 here against 90x48
        there, Filter 87x49 against 95x48. The cause was that this file wrote
        `{ padding: '12px', paddingX: '12px' }` instead of spreading
        `toolbarControlSx` — the shared toolbar-control surface `fieldMetrics`
        had already extracted for exactly this button — so it inherited none of
        the height, radius or border and landed on the `@repo/ui` Button's
        natural 49px.

        Two hand-rolls asserting parity in prose is precisely the failure mode
        §31 and §36 name. The assertion is now structural.

        Gap goes to `{ xs: 1, md: 1.5 }` (8px / 12px), matching `HeaderBar`. It
        was a flat 8px, so even the SPACING half of that old comment was wrong.
        12px is the button-group gap of §29, converted for this app's 8px base.
      */}
      <Stack
        direction={'row'}
        spacing={{ xs: 1, md: 1.5 }}
        alignItems={'center'}
        ml={1.5}
        sx={{ flexShrink: 0 }}
      >
        {/* Count behaviour — an INLINE pill inside the button, matching the
            platform, rather than label text or a corner overlay — lives in
            `CountBadge`, which `ToolbarControl` composes. `SortControl` renders
            its clear-X as the welded right half of a split button, so this
            Stack's 12px gap separates whole CONTROLS, never the two halves of
            one. See those two files. */}
        <FilterControl count={filterBadgeCount} onClick={onOpenFilter} subject={subject} />

        {showSort && (
          <SortControl
            count={sortBadgeCount}
            onClick={onOpenSort}
            subject={subject}
            showClear={showSortClear}
            onClear={onClearSort}
          />
        )}

        {onMoreOptionsClick && (
          <Button
            variant='iconBtn'
            onClick={onMoreOptionsClick}
            title='More options'
            sx={{
              backgroundColor: theme => theme.palette.grey[50],
              color: theme => theme.palette.text.primary,
              borderRadius: theme => `${(theme as any).shape?.borderRadius || 8}px`,
            }}
          >
            <EllipsisVertical size={22} />
          </Button>
        )}

        {viewToggle}
      </Stack>
    </Stack>
    </>
  );
};

export default SearchFilterSortBar;
